import {
  type StorefrontFeatureKey,
  type StorefrontNavLink,
  type StorefrontNavPlace,
  type StorefrontTerminology,
  visibleStorefrontNavLinks,
} from "@loot-os/api-contract";
import { NAV_ICONS } from "@loot-os/design-system/templates/shared/navIcons";
import type { IconData } from "@loot-os/icons/types";

/**
 * Where a link can appear: the header's links, the phone's tab bar, the signed-in shopper's
 * account menu and the footer's Store column.
 */
export type NavPlace = StorefrontNavPlace;

/** A link as the header, the menu, the tab bar and the footer render it, in the store's words. */
export interface NavLink extends StorefrontNavLink {
  icon: IconData;
}

export function visibleNavItems(
  hasFeature: (key: StorefrontFeatureKey) => boolean,
  terms: StorefrontTerminology,
): NavLink[] {
  return visibleStorefrontNavLinks(hasFeature, terms).map((link) => ({
    ...link,
    icon: NAV_ICONS[link.to],
  }));
}

/** The visible links that appear in `place`, in `STOREFRONT_NAV_LINKS` order. */
export function navLinksIn(links: readonly NavLink[], place: NavPlace): NavLink[] {
  return links.filter((link) => link.places.includes(place));
}

/** The most tabs the phone's tab bar draws, Home and Chat among them (`MobileTabBar`). */
export const TAB_BAR_MAX_TABS = 5;

/**
 * The links the phone's tab bar shows between Home and Chat: those placed in it, in order, as
 * many as fit beside Home and (when the store has it) Chat, so a store with every section on
 * keeps Chat and leaves off the last of them.
 */
export function tabBarLinks(links: readonly NavLink[], { chat }: { chat: boolean }): NavLink[] {
  return navLinksIn(links, "tabs").slice(0, TAB_BAR_MAX_TABS - 1 - (chat ? 1 : 0));
}
